import {useCallback, useEffect, useState, type ReactNode} from 'react'
import {useRouter} from 'next/router'
import {setCookie} from 'cookies-next'
import _ from 'lodash'
import toast from 'react-hot-toast'
// Custom hooks
import {useBoundRoute} from '../hooks/use-bound-route'
import {useCombinedAuthStatus} from '../hooks/use-combined-auth-status'
import {usePrevious} from '../hooks/use-previous'
// Layouts
import Section from './layout/section'
// Components
import FaIconLibrary from './icons/fa-icon-library'
import SubmissionHistory from './modals/submission-history'
import VotesHistory from './modals/votes-history'
import Badge from './badge'
import BoundLink from './bound-link'
import NavbarGroupList from './navbar-group-list'
import NavbarQuestionList from './navbar-question-list'
import SilentFederatedSignOutButton from './silent-federated-sign-out-button'
// Utils
import {smartSignIn} from '../utils/auth-helpers'
import {accessBallotDisplayType} from '../utils/ballot-access'
import {
  calculateGroupByBallotAndGroupSlug,
  calculateHasOnlyOneGroup,
} from '../utils/ballot-calculators'
import {logDataByLevel} from '../utils/logging-helpers'
import {convertHoursToSeconds} from '../utils/number-helpers'
import {seedInterstitialAdsByBallotfulGroup} from '../utils/question-helpers'
// Types
import type {Ballot, BallotfulGroup, SeededDestination} from '../types/ballot'
import type {Customer} from '../types/customer'
// Styles
import styles from './navbar.module.scss'

interface NavbarProps {
  ballot: Ballot | null
  customer: Customer | null
  currentWindowWidth: number
  sidebarIsOpen: boolean
  ballotIsPublished: boolean
  onPaginate?: ((page: number) => void) | null
  sendSidebarIsOpen: (isOpen: boolean) => void
  onUpdateModal: (modal: ReactNode, title: string, isOpen: boolean) => void
  backUrlOverride?: string | null
}

const Navbar = ({
  ballot,
  customer,
  currentWindowWidth,
  sidebarIsOpen,
  ballotIsPublished,
  onPaginate = null,
  sendSidebarIsOpen,
  onUpdateModal,
  backUrlOverride = null,
}: NavbarProps) => {
  const {voter, isLoadingVoter} = useCombinedAuthStatus()

  const router = useRouter()
  const boundRoute = useBoundRoute()

  const [groupSlug, setGroupSlug] = useState<string | null>(null)
  const isOneVotePerDay = ballot?.general?.one_vote_per_day_turn_on
  const ballotHasOnlyOneGroup = calculateHasOnlyOneGroup(ballot)
  const pathsWhichBackReturnsVoterHome = [
    '/groups',
    '/[group_slug]',
    '/advertise',
  ]
  const [currentGroup, setCurrentGroup] = useState<BallotfulGroup | null>(null)
  const [allQuestions, setAllQuestions] = useState<SeededDestination[]>([])
  const [voterMenuIsIn, setVoterMenuIsIn] = useState(false)
  const sendVoterMenuIsIn = (value: boolean): void => setVoterMenuIsIn(value)

  const toggleSidebar = () => {
    sendSidebarIsOpen(!sidebarIsOpen)
    setVoterMenuIsIn(false)
  }
  const toggleVoterMenu = () => {
    setVoterMenuIsIn(!voterMenuIsIn)
    sendSidebarIsOpen(false)
  }

  const [badgeValue, setBadgeValue] = useState(0)
  const username = voter?.email ? `@${_.first(voter?.email?.split('@'))}` : null

  useEffect(() => {
    if (!router.isReady) return
    if (!ballot) return

    const newGroupSlug = router.query?.group_slug
    setGroupSlug(typeof newGroupSlug === 'string' ? newGroupSlug : null)

    const allGroups = ballot.parent_categories

    const newCurrentGroup = (
      ballotHasOnlyOneGroup
        ? _.first(allGroups)
        : calculateGroupByBallotAndGroupSlug(ballot, String(newGroupSlug || ''))
    )
    setCurrentGroup(newCurrentGroup ? {...newCurrentGroup, ballot} as BallotfulGroup : null)
  }, [
    router.isReady,
    router.query,
    ballot,
  ])

  const previousBadgeValue = usePrevious(badgeValue)
  useEffect(() => {
    const displayType = accessBallotDisplayType(ballot)
    const newBadgeValue = displayType === 'photo-submissions-submit'
      ? voter?.submissions?.length ?? 0
      : voter?.votes?.length ?? 0
    setBadgeValue(newBadgeValue)
  }, [voter, ballot])

  useEffect(() => {
    if (!currentGroup || !currentGroup.ballot) return
    const newAllQuestions = seedInterstitialAdsByBallotfulGroup(currentGroup)
    setAllQuestions(newAllQuestions ?? [])
  }, [currentGroup])

  const [isSigningOut, setIsSigningOut] = useState(false)
  const sendIsSigningOut = (value: boolean): void => setIsSigningOut(value)

  const [isSigningIn, setIsSigningIn] = useState(false)
  const performSignIn = useCallback(async () => {
    if (isSigningIn || !ballot) return

    setIsSigningIn(true)
    try {
      setCookie('previous_pathname', router.asPath, {maxAge: convertHoursToSeconds(90), path: '/'})
      const allowsUnauthenticatedSignIn = ballot.general?.un_authenticated_emails_option_turn_on || false
      if (allowsUnauthenticatedSignIn) {
        const signInRoute = boundRoute('/auth/signin')
        if (signInRoute) router.push(signInRoute)
      } else {
        await smartSignIn({
          router,
          ballotId: ballot._id,
          citysparkId: customer?.subscription?.business_registration?.cityspark_id || null,
          voterId: voter?._id,
        })
      }
    } catch (error) {
      logDataByLevel({
        level: 'error',
        message: 'Error during sign-in',
        data: error,
      })
      toast.error('We couldn’t start sign-in. Please try again.')
    } finally {
      // Always clear the loading state. If smartSignIn resolves without
      // navigating (e.g. upstream sign-out returns false without throwing), the
      // button would otherwise stay stuck on "Signing in..." and block retries.
      // On a successful redirect the component unmounts, so this is harmless.
      setIsSigningIn(false)
    }
  }, [
    boundRoute,
    ballot,
    voter,
    isSigningIn,
  ])

  const handleSignIn = useCallback(() => {
    performSignIn()
  }, [performSignIn])

  const handleVoterHistoryClick = useCallback(() => {
    const votesHistoryModal = (
      <VotesHistory
        currentVoter={voter}
        isOneVotePerDay={isOneVotePerDay}
      />
    )
    onUpdateModal(votesHistoryModal, 'Votes History', true)
    toggleVoterMenu()
  }, [
    voter,
    isOneVotePerDay,
    toggleVoterMenu,
    onUpdateModal,
  ])
  const handleSubmissionHistoryClick = useCallback(() => {
    const submissionHistoryModal = (
      <SubmissionHistory
        currentVoter={voter}
      />
    )
    onUpdateModal(submissionHistoryModal, 'Submission History', true)
    toggleVoterMenu()
  }, [
    voter,
    toggleVoterMenu,
    onUpdateModal,
  ])

  return ballot
    ? <Section styleModifiers={['--is-nav', '--is-full-width']}>
      <div className={styles['navbar']}>
        {backUrlOverride &&
          <BoundLink
            href={backUrlOverride}
            className={`button is-black is-large is-flex-shrink-1 ${styles['__left-side-button']}`}
          >
            <FaIconLibrary icon='chevron-left' showTextAfter={currentWindowWidth > 480} />
          </BoundLink>
        }
        {!backUrlOverride && router.pathname !== '/' && <>
          {(ballotHasOnlyOneGroup || _.includes(pathsWhichBackReturnsVoterHome, router.pathname))
            ? <BoundLink
              href='/'
              className={`button is-black is-large is-flex-shrink-1 ${styles['__left-side-button']}`}
            >
              <FaIconLibrary icon='chevron-left' text='Home' showTextAfter={currentWindowWidth > 480} />
            </BoundLink>
            : <BoundLink
              href={groupSlug ? `/${groupSlug}` : '/'}
              className={`button is-black is-large is-flex-shrink-1 ${styles['__left-side-button']}`}
            >
              <FaIconLibrary icon='chevron-left' text={currentGroup?.parent_category_name} showTextAfter />
            </BoundLink>
          }
        </>}
        <div
          className='buttons has-addons is-flex-wrap-nowrap'
          style={{
            marginBottom: '0',
            marginLeft: router.pathname === '/' || router.pathname === '/groups' ? 'auto' : '0',
          }}
        >
          <div className={styles['__dropdown-container']}>
            <button
              onClick={voter ? toggleVoterMenu : handleSignIn}
              className={`button is-black is-large ${styles['__right-side-button']}`}
              style={{
                marginRight: '0',
                marginBottom: '0',
              }}
            >
              {(isLoadingVoter || isSigningIn) && <><FaIconLibrary icon='ellipsis' text={'Loading'} showTextAfter={currentWindowWidth > 480} /></>}
              {!isLoadingVoter && voter && <>
                <FaIconLibrary icon='user' text={username} showTextAfter={currentWindowWidth > 480} />
                <Badge previousValue={previousBadgeValue} value={badgeValue} backgroundColor="#06bf70" />
                <FaIconLibrary icon={voterMenuIsIn ? 'chevron-up' : 'chevron-down'} showTextAfter={false} />
              </>}
              {!isLoadingVoter && !voter && !isSigningIn && isSigningOut &&
                <FaIconLibrary icon='ellipsis' text='Signing out...' showTextAfter={currentWindowWidth > 480} />
              }
              {!isLoadingVoter && !voter && !isSigningIn && !isSigningOut &&
                <FaIconLibrary icon='sign-in' text='Sign In' showTextAfter={currentWindowWidth > 480} />
              }
            </button>
            {voterMenuIsIn &&
              <ul className={styles['__dropdown']}>
                {Boolean(voter) && <li className={styles['__dropdown-item']}><button className={styles['__dropdown-item-button']} onClick={handleVoterHistoryClick}>Votes history</button></li>}
                {Boolean(voter) && <li className={styles['__dropdown-item']}><button className={styles['__dropdown-item-button']} onClick={handleSubmissionHistoryClick}>Submission history</button></li>}
                <li className={styles['__dropdown-item']}><a href="https://www.cityspark.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className={styles['__dropdown-item-button']} aria-label="Open Privacy Policy in a new tab">Privacy Policy</a></li>
                <li className={styles['__dropdown-item']}><a href="https://www.cityspark.com/terms-of-use/" target="_blank" rel="noopener noreferrer" className={styles['__dropdown-item-button']} aria-label="Open Terms of Use in a new tab">Terms of Use</a></li>
                <SilentFederatedSignOutButton
                  isNavbarButton={true}
                  buttonText='Sign out'
                  isSigningOut={isSigningOut}
                  sendIsSigningOut={sendIsSigningOut}
                  sendVoterMenuIsIn={sendVoterMenuIsIn}
                />
              </ul>
            }
          </div>
          {!ballotHasOnlyOneGroup &&
            <BoundLink
              href='/'
              className={`button is-black is-large ${styles['__right-side-button']}`}
              style={{marginBottom: '0'}}
            >
              <FaIconLibrary icon='house' text='Home' showTextAfter={currentWindowWidth > 480} />
            </BoundLink>
          }
          {ballotIsPublished &&
            <button
              onClick={toggleSidebar}
              className={`button is-black is-large ${styles['__right-side-button']}`}
              data-open={sidebarIsOpen}
              style={{marginBottom: '0'}}
            >
              {sidebarIsOpen
                ? <FaIconLibrary icon='xmark' text='Browse' showTextBefore={currentWindowWidth > 480} />
                : <FaIconLibrary icon='bars' text='Browse' showTextBefore={currentWindowWidth > 480} />
              }
            </button>
          }
        </div>
        {ballotIsPublished && sidebarIsOpen &&
          <div className={styles['__sidebar']}>
            {!_.isEmpty(allQuestions) &&
              <div className={styles['__question-list']}>
                <NavbarQuestionList
                  ballot={ballot}
                  allQuestions={allQuestions}
                  onPaginate={onPaginate}
                />
              </div>
            }
            {!ballotHasOnlyOneGroup && !_.isEmpty(ballot.parent_categories) &&
              <div className={styles['__group-list']}>
                <NavbarGroupList
                  groups={ballot.parent_categories}
                  currentGroup={currentGroup}
                />
              </div>
            }
          </div>
        }
      </div>
    </Section>
    : null
}

export default Navbar
